import React, { useState } from "react";
import { Box, Typography, Button, TextField, IconButton, Paper } from "@mui/material";
import {
  motion,
  AnimatePresence,
} from "framer-motion";
import CloseIcon from "@mui/icons-material/Close";
import { joinCrew } from "../services/api/getAll";
import { useTranslation } from "react-i18next";
import bgImage from "../assets/kiki2.jpeg";

const JoinUs = () => {
  const [showForm, setShowForm] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [message, setMessage] = useState("");
  const [resume, setResume]: any = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    if (!fullName || !email || !phoneNumber || !message || !resume) {
      setErrorMessage(isRTL ? "يرجى ملء جميع الحقول ورفع السيرة الذاتية." : "Please fill out all fields and upload your resume.");
      setLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("fullName", fullName);
    formData.append("email", email);
    formData.append("phoneNumber", phoneNumber);
    formData.append("message", message);
    if (resume) {
      formData.append("resume", resume);
    }

    try {
      await joinCrew(formData);
      setSuccessMessage(isRTL ? "تم تقديم طلبك بنجاح!" : "Your application has been successfully submitted!");
      setFullName("");
      setEmail("");
      setPhoneNumber("");
      setMessage("");
      setResume(null);
    } catch (error) {
      setErrorMessage(
        isRTL
          ? "حدث خطأ أثناء إرسال طلبك. يرجى المحاولة مرة أخرى."
          : "There was an error submitting your application. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      id="join"
      sx={{
        py: { xs: 8, md: 16 },
        px: 2,
        background: "transparent",
        textAlign: "center",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        component={motion.div}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: [0.25, 1, 0.5, 1] }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          width: "100%",
          zIndex: 2,
          position: "relative",
        }}
      >
        <Typography
          variant="h3"
          fontWeight={900}
          mb={3}
          sx={{
            fontFamily: "Poppins, Ozen",
            background: "linear-gradient(135deg, #FFAE00 0%, #FF9100 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            textTransform: "uppercase",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            textShadow: "0 0 30px rgba(255, 174, 0, 0.1)",
          }}
        >
          {t("joinUs.joinThe")}&nbsp;
          <Box
            component="span"
            sx={{
              fontFamily: "Ozen",
              color: "#FFAE00",
              WebkitTextFillColor: "initial",
            }}
          >
            TowHelp
          </Box>
          &nbsp;{t("joinUs.Team")}
        </Typography>

        <Typography
          variant="body1"
          maxWidth="600px"
          mx="auto"
          mb={6}
          dir={isRTL ? "rtl" : "ltr"}
          sx={{
            color: "#ccc",
            fontFamily: "Poppins",
            lineHeight: 1.8,
            fontSize: "1.1rem",
            textAlign: "center",
          }}
        >
          {t("joinUs.description")}
        </Typography>

        <Button
          onClick={() => setShowForm(true)}
          variant="contained"
          className="glow-pulse-animation"
          sx={{
            px: 6,
            py: 2,
            fontSize: "1.05rem",
            borderRadius: "16px",
            fontWeight: "bold",
            fontFamily: "Poppins",
            background: "#FFAE00",
            color: "#000",
            boxShadow: "0 0 25px rgba(255, 174, 0, 0.4)",
            transition: "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
            "&:hover": {
              background: "#ffd455",
              boxShadow: "0 0 35px rgba(255, 174, 0, 0.7)",
              transform: "scale(1.05) translateY(-2px)",
            },
          }}
        >
          {t("joinUs.applyNow")}
        </Button>
      </Box>

      <AnimatePresence>
        {showForm && (
          <Box
            component={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              background: "rgba(0, 0, 0, 0.75)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <Paper
              elevation={0}
              className="premium-service-card"
              component={motion.div}
              initial={{ scale: 0.9, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ type: "spring", stiffness: 300, damping: 25 }}
              dir={isRTL ? "rtl" : "ltr"}
              sx={{
                width: "90%",
                maxWidth: 500,
                p: { xs: 4, md: 5 },
                position: "relative",
              }}
            >
              <IconButton
                onClick={() => setShowForm(false)}
                sx={{
                  position: "absolute",
                  top: 20,
                  right: isRTL ? "auto" : 20,
                  left: isRTL ? 20 : "auto",
                  color: "#FFAE00",
                  "&:hover": {
                    background: "rgba(255, 174, 0, 0.1)",
                  }
                }}
              >
                <CloseIcon />
              </IconButton>

              <Typography
                variant="h5"
                fontWeight={800}
                mb={4}
                sx={{ color: "#FFAE00", fontFamily: "Poppins", textAlign: isRTL ? "right" : "left" }}
              >
                {t("joinUs.applyTitle")}
              </Typography>

              <form onSubmit={handleSubmit}>
                <Box display="flex" flexDirection="column" gap={3}>
                  <TextField
                    label={t("joinUs.fullName")}
                    variant="outlined"
                    fullWidth
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    InputLabelProps={{ style: { color: "#aaa", fontFamily: "Poppins" } }}
                    sx={{
                      input: { color: "#fff", fontFamily: "Poppins" },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
                        "&:hover fieldset": { borderColor: "rgba(255, 174, 0, 0.5)" },
                        "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                      },
                    }}
                  />
                  <TextField
                    label={t("joinUs.email")}
                    variant="outlined"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    InputLabelProps={{ style: { color: "#aaa", fontFamily: "Poppins" } }}
                    sx={{
                      input: { color: "#fff", fontFamily: "Poppins" },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
                        "&:hover fieldset": { borderColor: "rgba(255, 174, 0, 0.5)" },
                        "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                      },
                    }}
                  />
                  <TextField
                    label={t("joinUs.phoneNumber")}
                    variant="outlined"
                    fullWidth
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    InputLabelProps={{ style: { color: "#aaa", fontFamily: "Poppins" } }}
                    sx={{
                      input: { color: "#fff", fontFamily: "Poppins" },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
                        "&:hover fieldset": { borderColor: "rgba(255, 174, 0, 0.5)" },
                        "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                      },
                    }}
                  />
                  <TextField
                    label={t("joinUs.message")}
                    variant="outlined"
                    multiline
                    rows={4}
                    fullWidth
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    InputLabelProps={{ style: { color: "#aaa", fontFamily: "Poppins" } }}
                    sx={{
                      textarea: { color: "#fff", fontFamily: "Poppins" },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "14px",
                        backgroundColor: "rgba(255, 255, 255, 0.02)",
                        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.1)" },
                        "&:hover fieldset": { borderColor: "rgba(255, 174, 0, 0.5)" },
                        "&.Mui-focused fieldset": { borderColor: "#FFAE00" },
                      },
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        mb: 1.5,
                        color: "#ccc",
                        fontFamily: "Poppins",
                        fontSize: "0.9rem",
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {t("joinUs.uploadResume")}
                    </Typography>
                    <label htmlFor="resume-upload">
                      <input
                        id="resume-upload"
                        type="file"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const file: any = e.target.files && e.target.files[0];
                          if (file) setResume(file);
                        }}
                        style={{ display: "none" }}
                      />
                      <Button
                        variant="outlined"
                        component="span"
                        sx={{
                          width: "100%",
                          justifyContent: "flex-start",
                          color: "#ccc",
                          borderColor: "rgba(255, 255, 255, 0.15)",
                          borderRadius: "14px",
                          py: 1.5,
                          px: 2.5,
                          textTransform: "none",
                          fontFamily: "Poppins",
                          "&:hover": {
                            borderColor: "#FFAE00",
                            background: "rgba(255, 174, 0, 0.05)",
                          },
                        }}
                      >
                        {resume ? resume.name : t("joinUs.chooseFile")}
                      </Button>
                    </label>
                  </Box>

                  <Button
                    variant="contained"
                    sx={{
                      mt: 2,
                      py: 1.8,
                      borderRadius: "14px",
                      background: "#FFAE00",
                      color: "#000",
                      fontWeight: "bold",
                      fontFamily: "Poppins",
                      "&:hover": {
                        background: "#ffd455",
                      },
                    }}
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? t("joinUs.submitting") : t("joinUs.submit")}
                  </Button>

                  {successMessage && (
                    <Typography sx={{ color: "#4caf50", mt: 2, fontWeight: 600, fontFamily: "Poppins" }}>
                      {successMessage}
                    </Typography>
                  )}
                  {errorMessage && (
                    <Typography sx={{ color: "#f44336", mt: 2, fontWeight: 600, fontFamily: "Poppins" }}>
                      {errorMessage}
                    </Typography>
                  )}
                </Box>
              </form>
            </Paper>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default JoinUs;
